import TriggerTeamAssistFollowUp from 'actions/team_assist/trigger_team_assist_follow_up';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_silent_handler';
import TeamAssistMessage from 'models/team_assist_message';

// Hard cap on the per-result body fed into the follow-up assist request.
// Mirrors the supernova-side `proposedActionResultMaxLen` so a misbehaving
// upstream tool can't blow out the LLM prompt with a multi-megabyte payload.
// Kept in lockstep with services/customerteamassist/application/customer_team_assist_prompt_actions.go.
export const PROPOSED_ACTION_RESULT_MAX_LEN = 2000;

/**
 * ExecuteTeamAssistProposedAction is invoked when the agent clicks Execute
 * on a `proposed_action` card. It:
 *   1. POSTs the captured tool name + arguments to the execute endpoint.
 *   2. Auto-fires a follow-up assist request carrying the just-executed
 *      result so the LLM can interpret it for the agent.
 *
 * Reject is purely client-side state and does not reach this action.
 *
 * The proposed-action message's `isExecuting` flag is the source of truth
 * for the card's loading state — set here while the request is in flight,
 * cleared on failure so the agent can retry. On success the executed
 * state is derived from the `follow_up_trigger` that
 * TriggerTeamAssistFollowUp persists.
 */
export default class ExecuteTeamAssistProposedAction {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, messageId, toolName, arguments: toolArguments }) {
    if (!customerId || !messageId || !toolName) {
      return;
    }
    if (!this.context.stores.customers.has({ id: customerId })) {
      return;
    }

    const { teamAssists } = this.context.stores.customers.storesFor(customerId);
    // Defense-in-depth in-flight guard. The card component also keeps a
    // synchronous ref latch to swallow double-clicks within a single
    // render frame, but this guard catches every other re-entry path —
    // a programmatic re-dispatch, a non-UI caller, or any future code
    // that drives this action without going through the proposed-action
    // card — because `setExecuting` writes the flag synchronously and the
    // next call sees it on the next `find`.
    const existing = teamAssists.find(messageId);
    if (!existing) {
      return;
    }
    if (existing.isExecuting) {
      return;
    }
    setExecuting(teamAssists, messageId, true);

    this.context.analytics.track('Team Assist Proposed Action Executed', {
      customerId,
      messageId,
      toolName,
    });

    this.context.gateways.teamAssist
      .executeProposedAction({ customerId, messageId }, { toolName, arguments: toolArguments || {} })
      .then(response => {
        const result = truncate(response && response.result, PROPOSED_ACTION_RESULT_MAX_LEN);
        const isError = Boolean(response && response.isError);
        setExecuting(teamAssists, messageId, false);
        this.context.executeAction(TriggerTeamAssistFollowUp, {
          customerId,
          proposedActionResults: [
            {
              messageId,
              toolName,
              isError,
              result,
            },
          ],
        });
      })
      .catch(err => {
        // Treat a thrown error (network blip, 4xx/5xx from supernova,
        // upstream MCP dispatch failure) as an execution failure — fire the
        // follow-up with isError=true and the extracted error message so
        // the LLM can interpret it for the agent in plain language, exactly
        // as it would for an isError=true response on the success path.
        // Without this, the user just sees the button reset with nothing
        // else to indicate what went wrong.
        //
        // Run cleanup (setExecuting → false, follow-up dispatch) FIRST and
        // BEFORE any operation that could throw — axios errors typically
        // carry circular references on `config`/`request`, which makes
        // naive `JSON.stringify(err)` throw a TypeError. A throw in the
        // catch handler would leave `isExecuting=true` and the proposed-
        // action card stuck.
        let extracted;
        try {
          extracted = extractErrorMessage(err);
        } catch (e) {
          extracted = 'The tool call failed and the error payload could not be inspected.';
        }
        setExecuting(teamAssists, messageId, false);
        this.context.executeAction(TriggerTeamAssistFollowUp, {
          customerId,
          proposedActionResults: [
            {
              messageId,
              toolName,
              isError: true,
              result: truncate(extracted, PROPOSED_ACTION_RESULT_MAX_LEN),
            },
          ],
        });
        handleCommonErrors(this.context, err);
      });
  }
}

// extractErrorMessage pulls a short human-readable summary out of whatever
// the gateway rejected with. Falls back to a generic line so the LLM
// always has something concrete to interpret. Order of preference matches
// what supernova's HTTP layer typically returns: a domain `errors` array,
// then the HTTP response body, then the JS error message.
function extractErrorMessage(err) {
  if (!err) {
    return 'The tool call failed before reaching the destination service.';
  }
  if (err.errors && Array.isArray(err.errors) && err.errors.length > 0) {
    return err.errors
      .map(e => {
        const parts = [];
        if (e.attr) parts.push(e.attr);
        if (e.code) parts.push(e.code);
        if (e.detail) parts.push(e.detail);
        return parts.length ? parts.join(': ') : safeStringify(e);
      })
      .join('; ');
  }
  if (err.response) {
    return typeof err.response === 'string' ? err.response : safeStringify(err.response);
  }
  if (err.message) {
    return err.message;
  }
  return String(err);
}

// safeStringify is a JSON.stringify that survives circular references —
// axios errors and similar payloads keep self-referencing `config`/
// `request` graphs that crash a naive stringify. On failure we fall back
// to String() (typically "[object Object]"), which is poor for content
// but sufficient as a log/debug breadcrumb and — crucially — never
// throws.
function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

function setExecuting(teamAssists, messageId, isExecuting) {
  const message = teamAssists.find(messageId);
  if (!message) {
    return;
  }
  teamAssists.addOrReplace(
    TeamAssistMessage.fromJs({
      ...message.toJs(),
      isExecuting,
    })
  );
}

function truncate(text, max) {
  if (typeof text !== 'string') {
    return '';
  }
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max - 1)}…`;
}
